import { AppModule } from '@/config/types'
import ReportingCall from '@/data/models/domain/reporting/calls/ReportingCall'
import ReportingMessage from '@/data/models/domain/reporting/messages/ReportingMessage'
import ReportingEmailTrend from '@/data/models/domain/reporting/email/ReportingEmailTrend'
import type { TmNamedRouteRecord } from '@/services/route/types'
import type { CleanModelsResolverParams } from '@/services/resolvers/cleanModelsResolver'
import { getOldRouteWithRedirect } from '@/utils/oldRouteRedirect'

export const userReportingSmsOverviewRouteName = 'user.reporting.sms.overview'
export const userReportingSmsTrendsRouteName = 'user.reporting.sms.trends'
export const userReportingSmsNumbersRouteName = 'user.reporting.sms.numbers'
export const userReportingSmsSubAccountsRouteName = 'user.reporting.sms.subAccounts'
export const userReportingSmsUsersRouteName = 'user.reporting.sms.users'

export const userReportingEmailOverviewRouteName = 'user.reporting.email.overview'
export const userReportingEmailTrendsRouteName = 'user.reporting.email.trends'
export const userReportingEmailUsersRouteName = 'user.reporting.email.users'
export const userReportingEmailSubAccountsRouteName = 'user.reporting.email.subAccounts'
export const userReportingEmailSendersRouteName = 'user.reporting.email.senders'
export const userReportingEmailLinksRouteName = 'user.reporting.email.links'

const reportingSmsRoute: TmNamedRouteRecord = {
  name: 'user.reporting.sms',
  path: 'sms',
  meta: {
    tags: ['REPORTING_BAR_LIST'],
  },
  redirect: { name: userReportingSmsOverviewRouteName },
  component: () => import('@/components/views/reporting/sms/ReportingSmsLayout.vue'),
  children: [
    {
      name: userReportingSmsOverviewRouteName,
      path: 'overview',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.sms.overview',
        permission: 'reportingOverview',
      },
      component: () => import('@/components/views/reporting/sms/ReportingSmsOverview.vue'),
    },
    {
      name: userReportingSmsTrendsRouteName,
      path: 'trends',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.sms.trends',
        permission: 'reportingMessages',
      },
      component: () => import('@/components/views/reporting/sms/ReportingSmsTrends.vue'),
    },
    {
      name: userReportingSmsNumbersRouteName,
      path: 'numbers',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.sms.numbers',
        permission: 'reportingNumbers',
      },
      component: () => import('@/components/views/reporting/sms/ReportingSmsNumbers.vue'),
    },
    {
      name: userReportingSmsSubAccountsRouteName,
      path: 'sub-accounts',
      meta: {
        tags: ['REPORTING_BAR_LIST'],
        tabTitle: 'pageTabTitle.user.reporting.subAccounts',
        permission: 'reportingSubAccounts',
        accessControlGroups: ['SubAccountsWorkflowStrategy'],
      },
      component: () => import('@/components/views/reporting/subAccounts/ReportingSubAccounts.vue'),
    },
    {
      name: userReportingSmsUsersRouteName,
      path: 'users',
      meta: {
        tags: ['REPORTING_BAR_LIST'],
        tabTitle: 'pageTabTitle.user.reporting.users',
        permission: 'reportingUsers',
        accessControlGroups: ['UserWorkflowStrategy'],
      },
      component: () => import('@/components/views/reporting/users/ReportingUsers.vue'),
    },
  ],
}

const reportingEmailRoute: TmNamedRouteRecord = {
  name: 'user.reporting.email',
  path: 'email',
  meta: {
    accessControlGroups: ['ReportingEmailStrategy'],
    tags: ['REPORTING_BAR_LIST'],
    permission: 'reportingEmails',
  },
  redirect: { name: userReportingEmailOverviewRouteName },
  component: () => import('@/components/views/reporting/email/ReportingEmailLayout.vue'),
  children: [
    {
      name: userReportingEmailOverviewRouteName,
      path: 'overview',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.email.overview',
        permission: 'reportingOverview',
      },
      component: () => import('@/components/views/reporting/email/ReportingEmailOverview.vue'),
    },
    {
      name: userReportingEmailTrendsRouteName,
      path: 'trends',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.email.trends',
      },
      component: () => import('@/components/views/reporting/email/ReportingEmailTrends.vue'),
    },
    {
      name: userReportingEmailUsersRouteName,
      path: 'users',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.email.users',
        permission: 'reportingUsers',
        accessControlGroups: ['UserWorkflowStrategy'],
      },
      component: () => import('@/components/views/reporting/email/ReportingEmailUsers.vue'),
    },
    {
      name: userReportingEmailSubAccountsRouteName,
      path: 'sub-accounts',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.email.subAccounts',
        permission: 'reportingSubAccounts',
        accessControlGroups: ['SubAccountsWorkflowStrategy'],
      },
      component: () => import('@/components/views/reporting/email/ReportingEmailSubAccounts.vue'),
    },
    {
      name: userReportingEmailSendersRouteName,
      path: 'senders',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.email.senders',
      },
      component: () => import('@/components/views/reporting/email/ReportingEmailSenders.vue'),
    },
    {
      name: userReportingEmailLinksRouteName,
      path: 'links',
      meta: {
        tabTitle: 'pageTabTitle.user.reporting.email.links',
      },
      component: () => import('@/components/views/reporting/email/ReportingEmailLinks.vue'),
    },
  ],
}

export const reportingCallsRoute: TmNamedRouteRecord = {
  name: 'user.reporting.calls',
  path: 'calls',
  meta: {
    tags: ['REPORTING_BAR_LIST'],
    tabTitle: 'pageTabTitle.user.reporting.calls',
    permission: 'reportingCalls',
  },
  component: () => import('@/components/views/reporting/calls/ReportingCalls.vue'),
}

export const reportingAuditLogsRoute: TmNamedRouteRecord = {
  name: 'user.reporting.auditLogs',
  path: 'audit-logs',
  meta: {
    tags: ['REPORTING_BAR_LIST'],
    tabTitle: 'pageTabTitle.user.reporting.auditLogs',
    permission: 'reportingAuditLogs',
    modules: [AppModule.AuditLogs],
  },
  component: () => import('@/components/views/reporting/auditLogs/ReportingAuditLogs.vue'),
}

export const reportingRoute: TmNamedRouteRecord = {
  name: 'user.reporting',
  path: 'reporting',
  meta: {
    tags: ['USER_TOP_MENU'],
    modules: [AppModule.Reporting],
    resolvers: [
      {
        service: 'CleanModelsResolver',
        params: {
          models: [ReportingMessage, ReportingCall, ReportingEmailTrend],
        } satisfies CleanModelsResolverParams,
      },
      { service: 'ReportingRedirectResolver' },
    ],
  },
  component: () => import('@/components/views/reporting/Reporting.vue'),
  children: [
    // need for save link from textmagic.com(in example https://support.textmagic.com/article/sms-reporting-overview/)
    getOldRouteWithRedirect('user.reporting.overview', 'overview', userReportingSmsOverviewRouteName),
    getOldRouteWithRedirect('user.reporting.messages', 'messages', userReportingSmsTrendsRouteName),
    getOldRouteWithRedirect('user.reporting.numbers', 'numbers', userReportingSmsNumbersRouteName),
    getOldRouteWithRedirect('user.reporting.subAccounts', 'sub-accounts', userReportingSmsSubAccountsRouteName),
    getOldRouteWithRedirect('user.reporting.users', 'users', userReportingSmsUsersRouteName),
    //-------
    reportingSmsRoute,
    reportingEmailRoute,
    reportingCallsRoute,
    reportingAuditLogsRoute,
  ],
}
